/**
 * Session Management for Wizard Tracking
 *
 * Manages a unique session ID that links all wizard submissions together.
 * Session persists in localStorage until the wizard is completed.
 */

const SESSION_KEY = 'aidfone_wizard_session_id';

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get existing session ID or create a new one
 * Session ID is stored in localStorage and persists across page refreshes
 */
export function getOrCreateSessionId(): string {
  // Check if we already have a session ID
  const existingId = localStorage.getItem(SESSION_KEY);

  if (existingId) {
    return existingId;
  }

  // Generate new session ID
  const newId = generateUUID();
  localStorage.setItem(SESSION_KEY, newId);

  return newId;
}

/**
 * Get current session ID without creating a new one
 * Returns null if no session exists
 */
export function getSessionId(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

/**
 * Clear the session ID (call after wizard completion)
 */
export function clearSessionId(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Client info from server API
 */
export interface ClientInfo {
  ip: string;
  userAgent: string;
  referrer: string | null;
}

/**
 * Fetch client info (IP address, user agent, referrer) from the server
 * The server can see the real IP address even through proxies
 */
export async function fetchClientInfo(): Promise<ClientInfo> {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const response = await fetch(`${apiUrl}/client-info`);

    if (!response.ok) {
      throw new Error('Failed to fetch client info');
    }

    return await response.json();
  } catch (error) {
    console.warn('Could not fetch client info:', error);
    // Return fallback values
    return {
      ip: 'unknown',
      userAgent: navigator.userAgent,
      referrer: document.referrer || null,
    };
  }
}
